import { useState, useMemo } from 'react';
import { Shield, ShieldAlert, KeyRound, FileText, Search, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import { useGetGuardrailsCatalog } from 'features/chat/hooks/use-agents';
import type { GuardrailCatalogItem } from 'features/chat/agent-chat-module/types/agent.service.type';

type AgentConfigAddGuardrailModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  configuredGuardrailIds: string[];
  onAddGuardrail: (item: GuardrailCatalogItem) => void;
  onRemoveGuardrail: (guardrailId: string) => void;
};

const getGuardrailMeta = (id: string, t: (key: string, options?: any) => string) => {
  switch (id) {
    case 'secret':
      return {
        title: t('GUARDRAIL_SECRET_TITLE', { defaultValue: 'Secret Detection' }),
        description: t('GUARDRAIL_SECRET_DESC', {
          defaultValue:
            'Detects and protects against exposure of API keys, private credentials, and secrets.',
        }),
        icon: KeyRound,
      };
    case 'keyword':
      return {
        title: t('GUARDRAIL_KEYWORD_TITLE', { defaultValue: 'Keyword Filter' }),
        description: t('GUARDRAIL_KEYWORD_DESC', {
          defaultValue:
            'Restricts answers containing specific standalone keywords and terms (e.g. C++, class).',
        }),
        icon: FileText,
      };
    default:
      return {
        title: id.charAt(0).toUpperCase() + id.slice(1).replace(/_/g, ' '),
        description: t('GUARDRAIL_GENERIC_DESC', {
          defaultValue: 'Enforces safety boundaries for agent interactions.',
        }),
        icon: ShieldAlert,
      };
  }
};

export function AgentConfigAddGuardrailModal({
  open,
  onOpenChange,
  configuredGuardrailIds,
  onAddGuardrail,
  onRemoveGuardrail,
}: AgentConfigAddGuardrailModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const { data: catalog = [], isLoading } = useGetGuardrailsCatalog({
    enabled: open,
  });

  const filteredCatalog = useMemo(() => {
    if (!searchQuery.trim()) return catalog;
    const q = searchQuery.toLowerCase().trim();
    return catalog.filter((item) => {
      const meta = getGuardrailMeta(item.id, t);
      return (
        item.id.toLowerCase().includes(q) ||
        meta.title.toLowerCase().includes(q) ||
        meta.description.toLowerCase().includes(q)
      );
    });
  }, [catalog, searchQuery, t]);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSearchQuery('');
    }
    onOpenChange(nextOpen);
  };

  const selectedCount = configuredGuardrailIds.length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[min(92dvh,100svh)] w-[calc(100vw-1.5rem)] max-w-[700px] flex-col overflow-hidden p-4 sm:p-5">
        <DialogHeader className="shrink-0">
          <DialogTitle className="pr-8 text-lg font-semibold leading-snug sm:text-2xl">
            {t('ADD_GUARDRAIL', { defaultValue: 'Add Guardrails' })}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            {t('BROWSE_AND_SELECT_GUARDRAILS_FOR_AGENT', {
              defaultValue:
                'Select guardrails from the catalog to enforce safety policies, content filtering, and secret protection.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 flex min-h-0 flex-1 flex-col space-y-4 sm:mt-6 sm:space-y-6">
          <div className="relative shrink-0">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={t('SEARCH_GUARDRAILS', { defaultValue: 'Search guardrails...' })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain sm:max-h-[380px]">
            {isLoading ? (
              Array.from({ length: 2 }).map((_, index) => (
                <Card key={`guardrail-skeleton-${index}`} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-10 w-10 rounded-lg" />
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-3 w-48" />
                      </div>
                    </div>
                    <Skeleton className="h-8 w-16" />
                  </div>
                </Card>
              ))
            ) : filteredCatalog.length === 0 ? (
              <div className="flex h-48 flex-col items-center justify-center text-center">
                <Shield className="mb-3 h-10 w-10 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery.trim()
                    ? t('NO_GUARDRAILS_MATCHING_SEARCH', {
                      defaultValue: 'No guardrails match your search query',
                    })
                    : t('NO_GUARDRAILS_AVAILABLE', {
                      defaultValue: 'No guardrails available in catalog',
                    })}
                </p>
              </div>
            ) : (
              filteredCatalog.map((item) => {
                const isSelected = configuredGuardrailIds.includes(item.id);
                const meta = getGuardrailMeta(item.id, t);
                const Icon = meta.icon || Shield;

                return (
                  <Card key={item.id} className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-white/10 dark:text-foreground">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">{meta.title}</span>
                            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                              {item.id}
                            </span>
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                            {meta.description}
                          </p>
                        </div>
                      </div>

                      <div>
                        {isSelected ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onRemoveGuardrail(item.id)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Check className="mr-1 h-3.5 w-3.5 text-primary" />
                            {t('ADDED', { defaultValue: 'Added' })}
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onAddGuardrail(item)}
                          >
                            {t('ADD', { defaultValue: 'Add' })}
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </div>

          <div className="shrink-0 text-sm text-muted-foreground">
            {selectedCount}{' '}
            {selectedCount === 1
              ? t('GUARDRAIL_SELECTED', { defaultValue: 'guardrail active' })
              : t('GUARDRAILS_SELECTED', { defaultValue: 'guardrails active' })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
